/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-runloop, ember/require-tagless-components */
import Component from '@ember/component';
import { scheduleOnce } from '@ember/runloop';
import {
  attributeBindings,
  classNames,
  tagName,
} from '@ember-decorators/component';

// D3 imports
import { select } from 'd3-selection';
import { scaleBand, scaleLinear, scaleOrdinal } from 'd3-scale';
import { max } from 'd3-array';
import { axisBottom, axisLeft } from 'd3-axis';
import tip from '@linc-technologies/linced-common/utils/d3-tip';
import { mapBy } from '@linc-technologies/linced-common/utils/arrays';

@tagName('svg')
@classNames('chart', 'chart-bar')
@attributeBindings('width', 'height')
export default class ChartBar extends Component {
  width = 480;
  height = 320;
  margin = Object.freeze({ top: 20, right: 20, bottom: 30, left: 40 });
  data = [];
  barClicked() {}

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // Schedule a call to our `drawCircles` method on Ember's "render" queue, which will
    // happen after the component has been placed in the DOM, and subsequently
    // each time data is changed.
    scheduleOnce('render', this, this.draw);
  }

  draw() {
    let plot = select(this.element);
    let data = this.data;
    let margin = this.margin;
    let width = this.width - margin.left - margin.right;
    let height = this.height - margin.top - margin.bottom;

    let x = scaleBand().rangeRound([0, width]);
    let y = scaleLinear().rangeRound([height, 0]);
    let colors = scaleOrdinal(this.colors);

    // Remove previous chart (if there is one), then append the new root element.
    plot.selectAll('*').remove();
    let g = plot
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');
    x.domain(mapBy(data, 'value'));
    y.domain([0, max(data, (d) => d.count)]);

    // maxCount will be the maximum `count` from the dataset, with a minimum value of 1.
    let maxCount = data.reduce(function (prev, item) {
      return Math.max(item.count, prev);
    }, 1);

    // Tooltip
    let tooltip = tip()
      .attr('class', 'd3-tip')
      .html((d) => d.tooltip || d.label);
    g.call(tooltip);

    g.append('g')
      .attr('class', 'axis axis--x')
      .attr('transform', 'translate(0,' + height + ')')
      .call(axisBottom(x));

    g.append('g')
      .attr('class', 'axis axis--y')
      .call(axisLeft(y).ticks(Math.min(5, maxCount)))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 6)
      .attr('dy', '0.71em')
      .attr('text-anchor', 'end');

    g.selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', function (d) {
        return x(d.value);
      })
      .attr('y', function (d) {
        return y(d.count);
      })
      .attr('fill', function (d, i) {
        return colors(i);
      })
      .attr('width', x.bandwidth())
      .attr('height', function (d) {
        return height - y(d.count);
      })
      .on('mouseover', tooltip.show)
      .on('mouseout', tooltip.hide)
      .on('click', (_, d) => this.barClicked(d));
  }
}
